import { CaretLeftOutlined, EditOutlined, HistoryOutlined, PlusOutlined, SearchOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Button, Empty, Form, Input, Modal, Space, Typography } from "antd";
import { descriptionRules, nameRules } from "common/FormValidationRules";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import Tooltip from "components/atoms/Tooltip";
import DataObjectsPreviewList from "components/molecules/DataObjectsPreviewList";
import DatasetGoldenNominalAction from "components/molecules/DatasetGoldenNominalAction";
import SurvivorshipTrustDrawer from "components/organisms/SurvivorshipTrustDrawer";
import DatasetPrecedenceTierSelect from "components/molecules/DatasetPrecedenceTierSelect";
import DatasetUtilitySource from "components/molecules/DatasetUtilitySource";
import DatasetGoldenNominalBanner from "components/molecules/DatasetGoldenNominalBanner";
import Comments from "containers/Comments";
import DataObjectRequestVerification from "containers/DataObjectRequestVerification";
import _ from "lodash";
import React, { Component } from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import actions from "store/actions";
import CypressTestIds from "../../../cypress/CypressTestIds";
import DataSetBatchUploadHistoryModal from "../DataSetBatchUploadHistoryModal";
import DatasetHeaderMetadata, { IDatasetHeaderMetadataState } from "./DatasetHeaderMetadata";

interface IMatchParams {
    tenant: string;
    businessarea: string;
}

type IManageDataSetProps = {
    action: "view" | "edit" | "create";
    permission: {
        canAdd: boolean;
        canEdit: boolean;
        canDelete?: boolean;
    };
    schemaModel: ISchemaModel;
    dataSet: IDataSet;
    dataObjectsResult: {
        pageKey?: string;
    };
    selectedDataObject?: IDataObject;
    dataObjects: IDataObject[];
    formErrors?: Record<string, string>;
    fetchingBatchUploadJobs?: boolean;
    undoJobInProgress?: boolean;
    jobList: unknown[];
    bulkVerification: boolean;
    loadingDataObjects: boolean;
    loadingNextPageDataObjects: boolean;
    onUpdateDataSet: (prevDs: IDataSet, newDs: IDataSet, callback: (() => void) | null) => void;
    onCreateDataSet: (newDs: IDataSet, callback: (() => void) | null) => void;
    onBulkUploadData: (name: string, source: unknown) => void;
    onGetNextDataObjects?: (name: string, key: string) => void;
    refreshDataObjects: () => void;
    onGetBatchUploadJobs: (id: string) => void;
    undoJob: (jobId: string, version: string) => void;
    onValuesChanged: (changed: any, existing: IDataSet) => void;
    onCancel: () => void;
    onCreateNewDataObject: () => void;
    onDataObjectSelected: (data: IDataObject) => void;
    onShowDataObjectHistory: (setName: string, dataOBjectId: string) => void;
    onDeleteDataObject: (id: string) => void;
    onOpenLocationForDataObject?: (data: IDataObject) => void;
    onOpenCommentsForDataObject?: (data: IDataObject) => void;
    onOpenAnnotationsForDataObject?: (data: IDataObject) => void;
    onVerifyEmailForDataObject?: (data: IDataObject) => void;
    onSwitchEditMode?: (dataSet: IDataSet) => void;
    onSearch?: (query: string) => void;
    searchResultDataObjects?: IDataObject[] | null;
    getCommentsPermission: () => void;
    getCommentsRequest?: (recordId: string) => void;
    userCommentsPermission: { canView: boolean; [key: string]: any };
    getCommentsPermissionResult: { success: boolean };
    commentsByRecordId?: Record<string, any[]>;
    onOpenPipelineBuilder?: () => void;
} & RouteComponentProps<IMatchParams>;

type IManageDataSetStates = {
    readOnlyView: boolean;
    afterSaveCallback: (() => void) | null;
    showSaveConfirmationModal: boolean;
    batchUploadModalVisible: boolean;
    isCommentsModalVisible: boolean;
    fetchingCommentsPermission: boolean;
    searchQuery: string;
    headerMetadata: IDatasetHeaderMetadataState;
};

type IGetFormErrors = {
    help: string;
    validateStatus: "" | "error" | "success" | "warning" | "validating" | undefined;
};

class ManageDataSet extends Component<IManageDataSetProps, IManageDataSetStates> {
    private formRef;
    private defaultDataSet;

    constructor(props: IManageDataSetProps) {
        super(props);
        this.state = {
            readOnlyView: this.props.action === "view",
            afterSaveCallback: null,
            batchUploadModalVisible: false,
            showSaveConfirmationModal: false,
            isCommentsModalVisible: false,
            fetchingCommentsPermission: false,
            searchQuery: "",
            headerMetadata: {
                rating: 0,
                tags: {},
            },
        };
        this.defaultDataSet = {
            schemaName: "",
            schemaDescription: "",
            dataSetName: "",
            dataSetDescription: "",
        };
        this.formRef = React.createRef<any>();
    }

    componentDidMount() {
        this.populateDataSet();
        this.props.getCommentsPermission();

        if (this.props.dataSet?.dataSetId && this.props.getCommentsRequest) {
            this.props.getCommentsRequest(this.props.dataSet.dataSetId);
        }

        if ((this.props as any).openComments) {
            this.setState({ isCommentsModalVisible: true });
        }
        try {
            const params = new URLSearchParams((this.props as any).location ? (this.props as any).location.search : window.location.search);
            if (params.get("dataset_comment") === "true") {
                this.setState({ isCommentsModalVisible: true });
            }
        } catch (e) {}
    }

    componentDidUpdate(prevProps: IManageDataSetProps) {
        if (this.props.dataSet?.dataSetId !== prevProps.dataSet?.dataSetId) {
            this.populateDataSet();

            if (this.props.dataSet?.dataSetId && this.props.getCommentsRequest) {
                this.props.getCommentsRequest(this.props.dataSet.dataSetId);
            }
        } else if (this.props.dataSet && prevProps.dataSet && this.props.dataSet.metadata !== prevProps.dataSet.metadata) {
            // Update metadata if dataset object itself was mutated with new metadata without changing ID
            this.setState({
                headerMetadata: {
                    rating: this.props.dataSet.metadata?.rating ?? 0,
                    tags: this.props.dataSet.metadata?.tags ?? {},
                },
            });
        }

        if (this.props.getCommentsPermissionResult !== prevProps.getCommentsPermissionResult) {
            this.setState({ fetchingCommentsPermission: false });
        }
        if ((this.props as any).openComments && !(prevProps as any).openComments) {
            this.setState({ isCommentsModalVisible: true });
        }
    }

    populateDataSet = () => {
        let dataSet = { ...this.defaultDataSet };
        if (this.props.schemaModel) {
            dataSet.schemaName = this.props.schemaModel.name;
            dataSet.schemaDescription = this.props.schemaModel.description;
        }
        if (this.props.dataSet) {
            dataSet.dataSetName = this.props.dataSet.dataSetName;
            dataSet.dataSetDescription = this.props.dataSet.dataSetDescription ?? "";

            // Initialize metadata from dataset
            if (this.props.dataSet.metadata) {
                this.setState({
                    headerMetadata: {
                        rating: this.props.dataSet.metadata?.rating ?? 0,
                        tags: this.props.dataSet.metadata?.tags ?? {},
                    },
                });
            }
        }

        this.formRef.current.resetFields();
        this.formRef.current.setFieldsValue(dataSet);
        this.defaultDataSet = this.formRef.current.getFieldsValue();
    };

    isReadOnlyView = () => {
        return this.state.readOnlyView || !(this.props.permission?.canEdit || this.props.permission?.canAdd);
    };

    isSaveAllowed = () => {
        if (this.props.action === "edit") {
            return this.props.permission?.canEdit;
        } else if (this.props.action === "create") {
            return this.props.permission?.canAdd;
        }
        return false;
    };

    onFormSubmit = (dataSet: IDataSet) => {
        if (!this.isSaveAllowed()) {
            return;
        }

        let metadataToSave = {
            metadata: {
                rating: dataSet.metadata?.rating ?? this.state.headerMetadata.rating,
                tags: dataSet.metadata?.tags ?? this.state.headerMetadata.tags,
            },
        };

        if (this.props.action === "edit") {
            let updatedDataSet = { ...this.props.dataSet, ...dataSet, ...metadataToSave };
            this.props.onUpdateDataSet(this.props.dataSet, updatedDataSet, this.state.afterSaveCallback);
        } else if (this.props.action === "create") {
            let newDataSet = { ...dataSet, ...metadataToSave };
            this.props.onCreateDataSet(newDataSet, this.state.afterSaveCallback);
        }
        this.setState({
            afterSaveCallback: null,
        });
    };

    onBulkUploadData = (source: unknown) => {
        let dataSet = this.formRef.current.getFieldsValue();
        if (this.props.action === "create") {
            this.setState({
                afterSaveCallback: () => this.props.onBulkUploadData(dataSet.dataSetName, source),
                showSaveConfirmationModal: true,
            });
        } else if (this.props.action === "edit") {
            if (_.isEqual(dataSet, this.defaultDataSet) === false) {
                this.setState({
                    afterSaveCallback: () => this.props.onBulkUploadData(dataSet.dataSetName, source),
                    showSaveConfirmationModal: true,
                });
            } else {
                this.props.onBulkUploadData(dataSet.dataSetName, source);
            }
        } else {
            this.props.onBulkUploadData(dataSet.dataSetName, source);
        }
    };

    onSaveConfirmOk = () => {
        this.setState(
            {
                showSaveConfirmationModal: false,
            },
            () => {
                this.formRef.current && this.formRef.current.submit();
            },
        );
    };

    onSaveConfirmCancel = () => {
        this.setState({
            showSaveConfirmationModal: false,
            afterSaveCallback: null,
        });
    };

    getFormErrors = (fieldName: string): IGetFormErrors | {} => {
        if (this.props.formErrors && this.props.formErrors[fieldName]) {
            return {
                help: this.props.formErrors[fieldName],
                validateStatus: "error",
            };
        }
        return {};
    };

    onDataObjectPageSelect = (pagination: IPagination) => {
        if (
            this.props.onGetNextDataObjects &&
            this.props.dataObjectsResult.pageKey &&
            this.props.dataObjects &&
            pagination.current * pagination.pageSize >= this.props.dataObjects.length
        ) {
            this.props.onGetNextDataObjects(this.props.dataSet.dataSetId, this.props.dataObjectsResult.pageKey);
        }
    };

    onSearchQueryChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ searchQuery: value });
        if (!value && this.props.onSearch) {
            this.props.onSearch("");
        }
    };

    onSearchSubmit = () => {
        if (this.props.onSearch) {
            this.props.onSearch(this.state.searchQuery);
        }
    };

    showBatchUploadJobsModal = () => {
        this.setState({
            batchUploadModalVisible: true,
        });
        this.props.onGetBatchUploadJobs(this.props.dataSet.dataSetId);
    };

    closeBatchUploadJobsModal = (jobUndoInitiated: boolean) => {
        this.setState({
            batchUploadModalVisible: false,
        });
        if (jobUndoInitiated) {
            this.props.refreshDataObjects();
        }
    };

    showCommentsModal = () => this.setState({ isCommentsModalVisible: true });

    closeCommentsModal = () => {
        this.setState({ isCommentsModalVisible: false });
        this.removeDatasetCommentQueryParam();
    };

    onSaveHeaderMetadata = (metadata: IDatasetHeaderMetadataState) => {
        this.setState({ headerMetadata: metadata }, () => {
            if (this.props.action === "edit") {
                // If editing an existing dataset, automatically trigger the form's submit mechanism
                // to save the active dataset including the new metadata over the update endpoint
                this.formRef.current?.submit();
            }
        });
    };

    removeDatasetCommentQueryParam = () => {
        try {
            const url = new URL(window.location.href);
            if (url.searchParams.has("dataset_comment")) {
                url.searchParams.delete("dataset_comment");
                window.history.replaceState({}, document.title, url.toString());
            }
        } catch (e) {
            // Unable to remove query param
            console.error(e);
        }
    };

    render() {
        const { dataSet, userCommentsPermission, commentsByRecordId } = this.props;
        const datasetComments = (dataSet?.dataSetId && commentsByRecordId ? commentsByRecordId[dataSet.dataSetId] : { Items: [] }) as { Items: any[] };
        const commentCount = datasetComments ? datasetComments.Items.length : 0;
        return (
            <>
                {this.state.batchUploadModalVisible && (
                    <DataSetBatchUploadHistoryModal
                        visible={this.state.batchUploadModalVisible}
                        loadingData={this.props.fetchingBatchUploadJobs}
                        isBusy={this.props.undoJobInProgress}
                        jobList={this.props.jobList}
                        onCancel={this.closeBatchUploadJobsModal}
                        onOk={this.closeBatchUploadJobsModal}
                        onUndoJob={this.props.undoJob}
                    ></DataSetBatchUploadHistoryModal>
                )}
                <Modal title="Save Dataset" centered={true} open={this.state.showSaveConfirmationModal} onOk={this.onSaveConfirmOk} onCancel={this.onSaveConfirmCancel}>
                    <p>There are unsaved changes. Do you want to save dataset?</p>
                </Modal>
                <Modal
                    title="Comments"
                    open={this.state.isCommentsModalVisible}
                    onCancel={this.closeCommentsModal}
                    footer={null}
                    width={800}
                    styles={{
                        body: { height: "60vh", overflow: "auto" },
                    }}
                >
                    {dataSet?.dataSetId && <Comments key={dataSet.dataSetId} recordId={dataSet.dataSetId} permission={userCommentsPermission} recordType="dataset" />}
                </Modal>
                <FlexContainerLayout
                    header={
                        <div className="flex w-full flex-col gap-4">
                            {this.props.action !== "create" && (
                                <DatasetGoldenNominalBanner
                                    schemaId={this.props.schemaModel?.schemaId}
                                    dataSetId={this.props.dataSet?.dataSetId}
                                    schemaName={this.props.schemaModel?.name}
                                    isGoldenNominal={this.props.dataSet?.isGoldenNominal}
                                />
                            )}
                            {this.props.action !== "create" && (
                                <DatasetHeaderMetadata
                                    dataSetId={this.props.dataSet?.dataSetId}
                                    dataSetName={this.props.dataSet?.dataSetName}
                                    initialMetadata={this.state.headerMetadata}
                                    commentCount={commentCount}
                                    latestComment={datasetComments && datasetComments.Items.length > 0 ? datasetComments.Items[0] : null}
                                    onSave={this.onSaveHeaderMetadata}
                                    onShowComments={userCommentsPermission?.canView ? this.showCommentsModal : undefined}
                                    readOnly={this.isReadOnlyView()}
                                />
                            )}
                            <Form
                                ref={this.formRef}
                                name="basic"
                                layout="vertical"
                                onFinish={this.onFormSubmit}
                                initialValues={this.defaultDataSet}
                                onValuesChange={this.props.onValuesChanged}
                                className="flex w-full flex-row gap-4"
                            >
                                <div className="flex w-full flex-1 flex-col gap-4">
                                    <div className="flex w-full flex-row justify-stretch gap-x-4">
                                        <Form.Item shouldUpdate={true} className="mb-0 flex-1">
                                            {() => {
                                                return (
                                                    this.formRef?.current && (
                                                        <div className="flex flex-auto flex-col rounded-md bg-gray-100 p-2 px-4">
                                                            <p className="text-sm font-bold text-rose-800">Schema Name</p>
                                                            <p className="font-bold">{this.formRef.current.getFieldValue(["schemaName"])}</p>
                                                        </div>
                                                    )
                                                );
                                            }}
                                        </Form.Item>
                                        <Form.Item shouldUpdate={true} className="mb-0 flex-1">
                                            {() => {
                                                return (
                                                    this.formRef?.current && (
                                                        <div className="flex flex-auto flex-col rounded-md bg-gray-100 p-2 px-4">
                                                            <p className="text-sm font-bold text-sky-800">Schema Description</p>
                                                            <p className="font-bold">{this.formRef.current.getFieldValue(["schemaDescription"])}</p>
                                                        </div>
                                                    )
                                                );
                                            }}
                                        </Form.Item>
                                    </div>
                                    <div className="flex w-full flex-row gap-x-4 [&_.ant-form-item-label]:!pb-1 [&_.ant-form-item-label]:text-xs [&_.ant-form-item-label]:font-bold">
                                        <Form.Item
                                            {...this.getFormErrors("dataSetName")}
                                            label="Dataset Name"
                                            name={["dataSetName"]}
                                            validateFirst={true}
                                            rules={nameRules}
                                            className="mb-0 flex-1"
                                            layout="vertical"
                                        >
                                            <Input readOnly={this.isReadOnlyView()} variant="filled" data-testid={CypressTestIds.DATASET_MANAGE_DATASET_NAME_INPUT} />
                                        </Form.Item>
                                        <Form.Item
                                            {...this.getFormErrors("dataSetDescription")}
                                            label="Dataset Description"
                                            name={["dataSetDescription"]}
                                            rules={descriptionRules}
                                            className="mb-0 flex-1"
                                            layout="vertical"
                                        >
                                            <Input readOnly={this.isReadOnlyView()} variant="filled" data-testid={CypressTestIds.DATASET_MANAGE_DATASET_DESCRIPTION_INPUT} />
                                        </Form.Item>
                                    </div>
                                </div>
                                <Space orientation="vertical">
                                    {!this.isReadOnlyView() && (
                                        <Button
                                            style={{ width: "100%" }}
                                            onClick={() => this.onBulkUploadData("systemconnection")}
                                            data-testid={CypressTestIds.DATASET_MANAGE_DATASET_CONNECT_TO_A_SYSTEM_BUTTON}
                                            disabled={this.isReadOnlyView() || !this.isSaveAllowed()}
                                        >
                                            Connect to a System
                                        </Button>
                                    )}
                                    {this.props.dataObjects && this.props.dataObjects.length > 0 && (
                                        <DataObjectRequestVerification
                                            disabled={
                                                !this.props.bulkVerification ||
                                                !this.props.schemaModel ||
                                                !this.props.schemaModel.fields ||
                                                this.props.schemaModel.fields.length === 0 ||
                                                this.props.loadingDataObjects ||
                                                !this.props.dataObjects ||
                                                this.props.dataObjects.length === 0
                                            }
                                            key={this.props.dataObjects[0].dataObjectId}
                                            dataObject={this.props.dataObjects[0]}
                                            schemaFields={(this.props.schemaModel && this.props.schemaModel.fields) || []}
                                            bulkVerification={this.props.bulkVerification}
                                        />
                                    )}
                                    {!this.isReadOnlyView() && (
                                        <Button
                                            style={{ width: "100%" }}
                                            type="primary"
                                            onClick={() => this.onBulkUploadData("file")}
                                            data-testid={CypressTestIds.DATASET_MANAGE_DATASET_LOAD_DATA_FILE_INPUT}
                                            disabled={this.isReadOnlyView() || !this.isSaveAllowed()}
                                        >
                                            Load data from a file
                                        </Button>
                                    )}
                                </Space>
                            </Form>
                        </div>
                    }
                    footer={
                        <div className="flex w-full flex-row gap-3">
                            <Button
                                onClick={this.showBatchUploadJobsModal}
                                disabled={this.props.action === "create"}
                                type="link"
                                className="border border-slate-100 bg-slate-50 hover:bg-gray-50"
                                data-testid={CypressTestIds.DATASET_BATCH_UPLOAD_HISTORY_BUTTON}
                            >
                                Batch Upload History <HistoryOutlined />
                            </Button>
                            <div className="flex-1" />
                            <Button
                                onClick={this.props.onCancel}
                                data-testid={CypressTestIds.DATASET_MANAGE_DATASET_CANCEL_BUTTON}
                                icon={this.isReadOnlyView() ? <CaretLeftOutlined /> : null}
                            >
                                {this.isReadOnlyView() ? "Back to List" : "Cancel"}
                            </Button>
                            {!this.isReadOnlyView() ? (
                                <>
                                    {this.props.action === "edit" && (
                                        <Button
                                            disabled={this.isReadOnlyView() || !this.isSaveAllowed()}
                                            type="primary"
                                            onClick={this.props.onCreateNewDataObject}
                                            data-testid={CypressTestIds.DATASET_MANAGE_DATASET_ADD_DATA_BUTTON}
                                        >
                                            <PlusOutlined /> Add Data
                                        </Button>
                                    )}
                                    <Button
                                        disabled={this.isReadOnlyView() || !this.isSaveAllowed()}
                                        onClick={() => this.formRef.current && this.formRef.current.submit()}
                                        htmlType="submit"
                                        type="primary"
                                        data-testid={CypressTestIds.DATASET_MANAGE_DATASET_SAVE_BUTTON}
                                    >
                                        Save
                                    </Button>
                                </>
                            ) : (
                                !!this.props.onSwitchEditMode && (
                                    <Button type="primary" onClick={() => this.props.onSwitchEditMode && this.props.onSwitchEditMode(this.props.dataSet)} icon={<EditOutlined />}>
                                        Edit Mode
                                    </Button>
                                )
                            )}
                            {this.props.action !== "create" && (
                                <DatasetGoldenNominalAction
                                    businessAreaId={this.props.schemaModel?.businessAreaId}
                                    schemaId={this.props.schemaModel?.schemaId}
                                    schemaName={this.props.schemaModel?.name}
                                    dataSetId={this.props.dataSet?.dataSetId}
                                    dataSetName={this.props.dataSet?.dataSetName}
                                    canEdit={this.props.permission?.canEdit}
                                    isGoldenNominal={this.props.dataSet?.isGoldenNominal}
                                />
                            )}
                            {this.props.action !== "create" && this.props.permission?.canEdit && (
                                <SurvivorshipTrustDrawer
                                    businessAreaId={this.props.schemaModel?.businessAreaId}
                                    schemaId={this.props.schemaModel?.schemaId}
                                    schemaName={this.props.schemaModel?.name}
                                    schemaFields={this.props.schemaModel?.fields || []}
                                />
                            )}
                            {this.props.action !== "create" && (
                                <DatasetPrecedenceTierSelect
                                    businessAreaId={this.props.schemaModel?.businessAreaId}
                                    schemaId={this.props.schemaModel?.schemaId}
                                    dataSetId={this.props.dataSet?.dataSetId}
                                    precedenceTier={this.props.dataSet?.precedenceTier}
                                    isGoldenNominal={this.props.dataSet?.isGoldenNominal}
                                    canEdit={this.props.permission?.canEdit}
                                />
                            )}
                            {this.props.action !== "create" && (
                                <DatasetUtilitySource
                                    businessAreaId={this.props.schemaModel?.businessAreaId}
                                    schemaId={this.props.schemaModel?.schemaId}
                                    dataSetId={this.props.dataSet?.dataSetId}
                                    dataSetName={this.props.dataSet?.dataSetName}
                                    datasetRole={this.props.dataSet?.datasetRole}
                                    isGoldenNominal={this.props.dataSet?.isGoldenNominal}
                                    truncate={this.props.dataSet?.truncate}
                                    canEdit={this.props.permission?.canEdit}
                                    canDelete={this.props.permission?.canDelete}
                                />
                            )}
                            {this.props.action !== "create" && this.props.onOpenPipelineBuilder && (
                                <Button type="primary" onClick={this.props.onOpenPipelineBuilder} icon={<ThunderboltOutlined />} data-testid="pipeline-open-button">
                                    Bulk Transformations
                                </Button>
                            )}
                            {this.props.action !== "create" && (
                                <Tooltip title="Feature coming soon">
                                    <Button type="primary" disabled={true} data-testid={CypressTestIds.DATASET_MANAGE_DATASET_EXPORT_BUTTON}>
                                        Export
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    }
                >
                    {this.props.action === "create" ? (
                        <div className="flex flex-1 items-center justify-center bg-slate-50">
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Dataset Creation">
                                <div className="max-w-[500px]">
                                    <Typography.Text>
                                        To begin, please give your dataset a name and a description. Then click the "Save" button located at the bottom right.
                                    </Typography.Text>
                                </div>
                            </Empty>
                        </div>
                    ) : (
                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                            <div className="shrink-0 px-2 py-2">
                                <Input
                                    placeholder="e.g. Find records where name contains John"
                                    value={this.state.searchQuery}
                                    onChange={this.onSearchQueryChange}
                                    onPressEnter={this.onSearchSubmit}
                                    allowClear
                                    suffix={<SearchOutlined className="cursor-pointer text-gray-400 hover:text-blue-500" onClick={this.onSearchSubmit} />}
                                />
                            </div>
                            <div className="relative min-h-0 flex-1 overflow-hidden">
                                <DataObjectsPreviewList
                                    showRecordCount={false}
                                    schemaFields={(this.props.schemaModel && this.props.schemaModel.fields) || []}
                                    dataObjectList={
                                        this.props.loadingDataObjects
                                            ? []
                                            : this.props.searchResultDataObjects != null
                                              ? this.props.searchResultDataObjects
                                              : this.props.dataObjects
                                    }
                                    loading={this.props.loadingDataObjects || this.props.loadingNextPageDataObjects}
                                    onPageSelect={this.onDataObjectPageSelect}
                                    onDataObjectSelected={this.props.onDataObjectSelected}
                                    onOpenLocation={this.props.onOpenLocationForDataObject}
                                    showDiscussionIndicators
                                    onOpenComments={this.props.onOpenCommentsForDataObject}
                                    onOpenAnnotations={this.props.onOpenAnnotationsForDataObject}
                                    onVerifyEmail={this.props.onVerifyEmailForDataObject}
                                    onShowDataObjectHistory={(dataObjectId: string) => this.props.onShowDataObjectHistory(this.props.dataSet.dataSetName, dataObjectId)}
                                    onDeleteDataObject={this.props.onDeleteDataObject}
                                    recordCount={this.props.searchResultDataObjects != null ? this.props.searchResultDataObjects.length : (this.props.dataObjects?.length ?? 0)}
                                    selectedDataObject={this.props.selectedDataObject}
                                />
                            </div>
                        </div>
                    )}
                </FlexContainerLayout>
            </>
        );
    }
}

const mapStateToProps = (state: any) => ({
    getCommentsPermissionResult: state.comments.getCommentsPermissionResult,
    userCommentsPermission: state.comments.userCommentsPermission,
    commentsByRecordId: state.comments.commentsByRecordId,
});

const mapDispatchToProps = (dispatch: any) => ({
    getCommentsPermission: () => dispatch(actions.getCommentsPermissionRequest()),
    getCommentsRequest: (recordId: string) => dispatch(actions.getCommentsRequest(recordId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ManageDataSet));
